import { rankItem } from "@tanstack/match-sorter-utils";
import { FilterFn, createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  formCategoryLabel,
  formTypeTheme,
  globalTeamUri,
} from "@smart/bridge-types-basic";
import { useStorage } from "@smart/itops-hooks-dom";
import { loadLocale } from "@smart/itops-locale-dom";
import {
  Badge,
  BadgeProps,
  CellWrapper,
  MultipleLabel,
  TableProps,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";
import { extractId } from "@smart/itops-types-basic";
import { formatDateTime } from "@smart/itops-utils-basic";

import { ListFormRow } from "./data";
import { FormMenu } from "./menu";
import { RestoreForm } from "./restore-form";
import { useListState } from "./review-view";
import { listStorageKeys, searchSchema } from "./storage-keys";
import { hasCustomReminderMessage } from "../../shared/custom-reminder-message";
import { usePrintForm } from "../shared/print-form";
import { GqlDeletedForm, GqlForm } from "../types";

const sortSchema = z.array(z.object({ id: z.string(), desc: z.boolean() }));

const fuzzyFilter: FilterFn<ListFormRow> = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value, { threshold: 2 });
  addMeta({
    itemRank,
  });

  return itemRank.passed;
};

const reminderMessageSource = (row: ListFormRow) => {
  if ((row as GqlDeletedForm).deletedAt) return undefined;
  return hasCustomReminderMessage(row as GqlForm) ? "custom" : "default";
};

export const useFormColumns = () => {
  const { dateDisplayFormat, terms } = loadLocale();
  const { "LINK-3736": automaticRemindersFlag } = useFeatureFlags();
  const webEnquiryTooltip = `Web ${terms.enquiry.title}`;
  const [filterValue, setFilterValue] = useListState("search", {
    defaultValue: "",
    key: listStorageKeys.search,
    schema: searchSchema,
    storage: "session",
  });
  const [columnSort, setColumnSort] = useStorage({
    defaultValue: [{ id: "updatedAt", desc: true }],
    key: "form-builder-list-sort",
    schema: sortSchema,
    storage: "local",
  });
  const [deletingForm, setDeletingForm] = useState<GqlForm>();
  const [duplicatingForm, setDuplicatingForm] = useState<GqlForm>();
  const [printingForm, setPrintingForm] = useState<GqlForm>();

  const { printFormContent } = usePrintForm({
    formUri: printingForm?.uri,
    onPrinted: () => setPrintingForm(undefined),
  });

  const navigate = useNavigate();

  const columns = useMemo(() => {
    const col = createColumnHelper<ListFormRow>();

    return [
      col.accessor("formTitle", {
        header: "Name",
        cell: ({ getValue }) => (
          <CellWrapper label={getValue()} size="medium" />
        ),
      }),
      col.accessor("formCategory", {
        header: "Form Type",
        enableGlobalFilter: false,
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-small">
            <Badge
              label={formCategoryLabel[getValue()]}
              theme={formTypeTheme[getValue()]}
            />
            {row.original.hasSubmissionLink &&
              row.original.formCategory === "lead" && (
                <Tooltip text={webEnquiryTooltip} offsetBy={8} placement="top">
                  <Badge
                    label=""
                    iconLeft="regularGlobe"
                    theme="pink"
                    dataTestId="form-list-web-enquiry-badge"
                  />
                </Tooltip>
              )}
          </div>
        ),
      }),
      col.accessor("matterTypeList", {
        header: "Matter Type",
        cell: ({ row }) => (
          <MultipleLabel
            labels={[
              row.original.matterTypeNames,
              row.original.matterTypeLocations,
            ]}
            maxVisibleItems={1}
            dividerClassName="border-neutral-850"
          />
        ),
      }),
      col.accessor("updatedAt", {
        header: "Last Updated",
        enableGlobalFilter: false,
        sortDescFirst: true,
        cell: ({ getValue }) => (
          <CellWrapper label={formatDateTime(getValue(), dateDisplayFormat)} />
        ),
      }),
      col.accessor("createdBy", {
        header: "Created By",
        enableGlobalFilter: false,
        cell: (c) => {
          const providerInfo = useProviderInfo();
          const label =
            c.row.original.teamUri === globalTeamUri
              ? providerInfo?.label
              : c.getValue();
          return <CellWrapper label={label || ""} />;
        },
      }),
      ...(automaticRemindersFlag
        ? [
            col.accessor(reminderMessageSource, {
              id: "reminderMessage",
              header: "Reminder Message",
              enableGlobalFilter: false,
              sortUndefined: 1,
              cell: ({ getValue }) => {
                const source = getValue();
                if (!source) return null;
                return source === "custom" ? (
                  <Badge label="Custom" theme="information" />
                ) : (
                  <Badge label="Firm default" theme="emphasis" />
                );
              },
            }),
          ]
        : []),
      col.accessor("active", {
        header: "Status",
        enableGlobalFilter: false,
        sortUndefined: -1,
        cell: (c) => {
          let props: BadgeProps = { label: "Inactive", theme: "emphasis" };
          if (
            c.row.original.aiUserFeedback?.status === "generating" ||
            c.row.original.aiUserFeedback?.status === "generated"
          )
            props = {
              label: "Draft",
              iconRight: "regularSparkles",
              theme: "branding",
            };
          if (c.row.original.active)
            props = { label: "Active", theme: "success" };
          if ((c.row.original as GqlDeletedForm).deletedAt)
            props = { label: "Deleted", theme: "destructive" };

          return <Badge {...props} />;
        },
      }),
      col.display({
        id: "menu",
        meta: { action: true },
        cell: ({ row }) =>
          (row.original as GqlDeletedForm).deletedAt ? (
            <RestoreForm deletedForm={row.original as GqlDeletedForm} />
          ) : (
            <FormMenu
              form={row.original as GqlForm}
              hasSubmissionLink={row.original.hasSubmissionLink}
              submissionLinkHashes={row.original.submissionLinkHashes}
              setDeletingForm={setDeletingForm}
              setDuplicatingForm={setDuplicatingForm}
              setPrintingForm={setPrintingForm}
            />
          ),
      }),
    ];
  }, [webEnquiryTooltip, automaticRemindersFlag]);

  const globalFilter: TableProps<ListFormRow>["globalFilter"] = {
    state: filterValue,
    onChange: setFilterValue,
    fn: fuzzyFilter,
  };

  const select: TableProps<ListFormRow>["select"] = {
    state: {},
    onChange: () => {},
  };

  const sort: TableProps<ListFormRow>["sort"] = {
    state: columnSort,
    onChange: setColumnSort,
  };

  const onRowClick = (row: ListFormRow) => {
    navigate(`/embedded/builder/${extractId(row.uri)}`);
  };

  return {
    columns,
    globalFilter,
    select,
    sort,
    deletingForm,
    setDeletingForm,
    duplicatingForm,
    setDuplicatingForm,
    onRowClick,
    printFormContent,
  };
};
